package es.cragma.fichajefacil.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.model.ClockInStatus
import es.cragma.fichajefacil.model.UserRole
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*
import fichafacil.shared.generated.resources.Res
import fichafacil.shared.generated.resources.app_logo
import fichafacil.shared.generated.resources.cragma_logo_blue
import fichafacil.shared.generated.resources.cragma_logo_white
import fichafacil.shared.generated.resources.ic_apple_logo
import fichafacil.shared.generated.resources.ic_google_logo
import org.jetbrains.compose.resources.painterResource

@Composable
fun CragmaHeader(
    modifier: Modifier = Modifier,
    title: String = "Fichaje Facil",
    subtitle: String = "Sistema Inteligente de Fichaje y Control Horario",
    isDarkBackground: Boolean = false
) {
    val isDark = isSystemInDarkTheme() || isDarkBackground

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Official Cragma brand logo ABOVE the title
        Image(
            painter = painterResource(if (isDark) Res.drawable.cragma_logo_white else Res.drawable.cragma_logo_blue),
            contentDescription = "Desarrollado por CRAGMA",
            modifier = Modifier.height(36.dp),
            contentScale = ContentScale.Fit
        )

        Spacer(modifier = Modifier.height(14.dp))

        Text(
            text = title,
            fontSize = 32.sp,
            fontWeight = FontWeight.ExtraBold,
            color = if (isDark) Color.White else MaterialTheme.colorScheme.onSurface,
            textAlign = TextAlign.Center
        )

        Spacer(modifier = Modifier.height(4.dp))

        Text(
            text = subtitle,
            fontSize = 13.sp,
            color = if (isDark) Color(0xFF94A3B8) else MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun CragmaBrandLogo(modifier: Modifier = Modifier, forceWhite: Boolean = false) {
    val isDark = isSystemInDarkTheme() || forceWhite
    Image(
        painter = painterResource(if (isDark) Res.drawable.cragma_logo_white else Res.drawable.cragma_logo_blue),
        contentDescription = "CRAGMA",
        modifier = modifier.height(18.dp),
        contentScale = ContentScale.Fit
    )
}

@Composable
fun CompanyLogoBadge(
    logoUrl: String?,
    companyName: String,
    modifier: Modifier = Modifier.size(36.dp)
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            if (!logoUrl.isNullOrBlank()) {
                Text(
                    text = logoUrl,
                    fontSize = 18.sp
                )
            } else {
                Text(
                    text = companyName.take(2).uppercase(),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
fun RoleBadge(role: UserRole, currentLanguage: AppLanguage = AppLanguage.SPANISH) {
    val (bgColor, textColor) = when (role) {
        UserRole.GLOBAL_ADMIN -> Pair(Color(0xFF6B21A8), Color.White)
        UserRole.COMPANY_ADMIN -> Pair(CragmaNavyDark, Color.White)
        UserRole.HR -> Pair(Color(0xFF0284C7), Color.White)
        UserRole.TEAM_LEADER -> Pair(Color(0xFF0D9488), Color.White)
        UserRole.WORKER -> Pair(Color(0xFF475569), Color.White)
    }

    val displayName = when (role) {
        UserRole.GLOBAL_ADMIN -> if (currentLanguage == AppLanguage.ENGLISH) "Global Admin" else "Administrador Global"
        UserRole.COMPANY_ADMIN -> if (currentLanguage == AppLanguage.ENGLISH) "Company Admin" else "Administrador Empresa"
        UserRole.HR -> if (currentLanguage == AppLanguage.ENGLISH) "Human Resources" else "Recursos Humanos"
        UserRole.TEAM_LEADER -> if (currentLanguage == AppLanguage.ENGLISH) "Team Leader" else "Responsable de Equipo"
        UserRole.WORKER -> if (currentLanguage == AppLanguage.ENGLISH) "Worker" else "Trabajador"
    }

    Surface(
        color = bgColor,
        shape = RoundedCornerShape(20.dp)
    ) {
        Text(
            text = displayName,
            color = textColor,
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
        )
    }
}

@Composable
fun StatusBadge(status: ClockInStatus, currentLanguage: AppLanguage = AppLanguage.SPANISH) {
    val s = LocalizedStrings.get(currentLanguage)
    val (bgColor, textColor, label) = when (status) {
        ClockInStatus.NOT_STARTED -> Triple(Color(0xFFE2E8F0), Color(0xFF475569), s.statusNotStarted)
        ClockInStatus.WORKING -> Triple(Color(0xFFD1FAE5), Color(0xFF065F46), s.statusWorking)
        ClockInStatus.ON_BREAK -> Triple(Color(0xFFFEF3C7), Color(0xFF92400E), s.statusOnBreak)
        ClockInStatus.FINISHED -> Triple(Color(0xFFFEE2E2), Color(0xFF991B1B), s.statusFinished)
    }

    Surface(
        color = bgColor,
        shape = RoundedCornerShape(16.dp)
    ) {
        Text(
            text = label,
            color = textColor,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
        )
    }
}

@Composable
fun VerificationBadge(isVerified: Boolean, currentLanguage: AppLanguage = AppLanguage.SPANISH) {
    val isEnglish = currentLanguage == AppLanguage.ENGLISH
    val (bgColor, textColor, text) = if (isVerified) {
        Triple(
            Color(0xFFDCFCE7),
            Color(0xFF15803D),
            if (isEnglish) "✓ Authenticated (With Location)" else "✓ Autenticado (Con Ubicación)"
        )
    } else {
        Triple(
            Color(0xFFFEF3C7),
            Color(0xFFB45309),
            if (isEnglish) "⚠ Unverified (Without Location)" else "⚠ No Autenticado (Sin Ubicación)"
        )
    }

    Surface(
        color = bgColor,
        shape = RoundedCornerShape(12.dp)
    ) {
        Text(
            text = text,
            color = textColor,
            fontSize = 11.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
        )
    }
}

@Composable
fun LegalNoteBanner(modifier: Modifier = Modifier, currentLanguage: AppLanguage = AppLanguage.SPANISH) {
    val s = LocalizedStrings.get(currentLanguage)
    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color(0xFFEFF6FF)),
        shape = RoundedCornerShape(12.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.Top
        ) {
            Text(
                text = "⚖",
                fontSize = 18.sp,
                modifier = Modifier.padding(end = 8.dp)
            )
            Column {
                Text(
                    text = s.breakNormativeTitle,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF1E40AF)
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = s.breakNormativeText,
                    fontSize = 11.sp,
                    color = Color(0xFF1E3A8A),
                    lineHeight = 15.sp
                )
            }
        }
    }
}

// Crisp Action Button with Border
@Composable
fun StyledButtonWhite(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val isDark = isSystemInDarkTheme()
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier
            .fillMaxWidth()
            .height(50.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = if (isDark) MaterialTheme.colorScheme.surface else Color.White,
            contentColor = if (isDark) Color.White else CragmaNavyDark,
            disabledContainerColor = Color(0xFFE2E8F0),
            disabledContentColor = Color(0xFF94A3B8)
        ),
        border = BorderStroke(1.dp, if (isDark) Color(0xFF334155) else Color(0xFFE2E8F0)),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(12.dp)
    ) {
        Text(
            text = text,
            fontWeight = FontWeight.Bold,
            fontSize = 15.sp
        )
    }
}

// Red Danger Button (End shift / Stop)
@Composable
fun StyledButtonRed(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier
            .fillMaxWidth()
            .height(50.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = CragmaRedDanger,
            contentColor = Color.White
        ),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(12.dp)
    ) {
        Text(
            text = text,
            fontWeight = FontWeight.Bold,
            fontSize = 15.sp
        )
    }
}

// Dark Navy / Primary Button
@Composable
fun StyledButtonPrimary(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier
            .fillMaxWidth()
            .height(50.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = MaterialTheme.colorScheme.primary,
            contentColor = MaterialTheme.colorScheme.onPrimary
        ),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(12.dp)
    ) {
        Text(
            text = text,
            fontWeight = FontWeight.Bold,
            fontSize = 15.sp
        )
    }
}

// Official Styled Google Sign-In Button
@Composable
fun GoogleSignInButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    text: String = "Continuar con Google"
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier
            .fillMaxWidth()
            .height(50.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.White,
            contentColor = Color(0xFF1F2937)
        ),
        border = BorderStroke(1.dp, Color(0xFFE5E7EB)),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(12.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Image(
                painter = painterResource(Res.drawable.ic_google_logo),
                contentDescription = "Google Logo",
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = text,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
                color = Color(0xFF1F2937)
            )
        }
    }
}

// Official Styled Apple Sign-In Button
@Composable
fun AppleSignInButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    text: String = "Iniciar sesión con Apple"
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier
            .fillMaxWidth()
            .height(50.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.Black,
            contentColor = Color.White
        ),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(12.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Image(
                painter = painterResource(Res.drawable.ic_apple_logo),
                contentDescription = "Apple Logo",
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = text,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
                color = Color.White
            )
        }
    }
}

// Digital Timer Display
@Composable
fun DigitalTimerDisplay(
    millis: Long,
    subtitle: String = "TIEMPO TRABAJADO"
) {
    val totalSeconds = millis / 1000
    val hours = totalSeconds / 3600
    val minutes = (totalSeconds % 3600) / 60
    val seconds = totalSeconds % 60

    val timeFormatted = "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .fillMaxWidth()
            .background(CragmaNavyDark, RoundedCornerShape(16.dp))
            .padding(vertical = 20.dp, horizontal = 16.dp)
    ) {
        Text(
            text = subtitle,
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            color = CragmaBlueSoft,
            letterSpacing = 1.5.sp
        )

        Spacer(modifier = Modifier.height(4.dp))

        Text(
            text = timeFormatted,
            fontSize = 42.sp,
            fontWeight = FontWeight.ExtraBold,
            color = Color.White,
            letterSpacing = 2.sp
        )
    }
}
